import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { setAdminTokens } from "@/lib/adminSession";
import BrandLogo from "@/components/common/BrandLogo";
import { useForceDarkTheme } from "@/lib/theme";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const inputCls =
  "w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-primary";

/**
 * Admin login, mounted at /admin/login and never linked from the public site.
 * Submits to /api/admin-auth/login (separate cookie, separate collection).
 */
export default function AdminLogin() {
  const nav = useNavigate();
  const { admin, setAdmin } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useForceDarkTheme();

  useEffect(() => {
    if (admin && admin.id) nav("/admin/dashboard", { replace: true });
  }, [admin, nav]);

  const valid = EMAIL_RE.test(email.trim()) && password.length >= 4;

  async function signIn(e) {
    e?.preventDefault();
    if (!valid) {
      setError("Enter email and password.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/api/admin-auth/login", {
        email: email.trim().toLowerCase(),
        password,
      });
      if (!data?.admin) {
        setError("This account is not an admin.");
        return;
      }
      if (data.admin_access_token) {
        setAdminTokens(data.admin_access_token, data.admin_refresh_token);
      }
      setAdmin(data.admin);
      nav("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(getErrorMessage(err) || "Sign in failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground"
      data-testid="admin-login-page"
    >
      <form
        onSubmit={signIn}
        className="w-full max-w-md rounded-2xl border border-border bg-surface/70 p-6 shadow-panel"
        data-testid="admin-login-form"
      >
        <BrandLogo size="md" />
        <h1 className="mt-5 text-2xl font-semibold">PaisaCX Admin</h1>
        <p className="mt-1 text-sm text-muted-foreground">Admin only — traders sign in on the main site with OTP.</p>
        <label className="mt-6 block text-xs text-muted-foreground" htmlFor="admin-email">
          Email
        </label>
        <input
          id="admin-email"
          type="email"
          autoComplete="username"
          className={`mt-1.5 ${inputCls}`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@paisacx.io"
          data-testid="admin-login-email"
        />
        <label className="mt-4 block text-xs text-muted-foreground" htmlFor="admin-password">
          Password
        </label>
        <div className="mt-1.5 flex gap-2">
          <input
            id="admin-password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            className={inputCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-login-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="shrink-0 rounded-lg border border-border px-3 text-xs text-muted-foreground hover:text-foreground"
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
        <button
          type="submit"
          disabled={busy || !valid}
          className="brand-gradient mt-5 w-full rounded-lg py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-40"
          data-testid="admin-login-submit"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
        {error && (
          <p className="mt-3 text-xs text-down" data-testid="admin-login-error">
            {error}
          </p>
        )}
      </form>
    </div>
  );
}
